/**
 * MFA device and session management API service
 */
import api, { getErrorMessage } from './api'
import type {
  MFADevice,
  MFAEnrollRequest,
  MFAEnrollResponse,
  ReauthData,
  UserSession,
} from '../types'

// ===== Admin MFA management =====

/**
 * List a user's authenticators. Removal is a soft delete, so `includeInactive`
 * is what surfaces deactivated devices — without it they are invisible and
 * unrestorable.
 */
export async function adminListMFADevices(
  userId: number,
  includeInactive = false,
): Promise<MFADevice[]> {
  try {
    const response = await api.get<MFADevice[]>(`/v1/users/${userId}/mfa/devices`, {
      params: includeInactive ? { include_inactive: true } : undefined,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function adminDeleteMFADevice(
  userId: number,
  deviceId: number,
  reauthToken?: string | null,
): Promise<void> {
  try {
    await api.delete(`/v1/users/${userId}/mfa/devices/${deviceId}`, {
      headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined,
    })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Restore a soft-deleted authenticator. Same re-auth gate as deactivating one. */
export async function adminReactivateMFADevice(
  userId: number,
  deviceId: number,
  reauthToken?: string | null,
): Promise<void> {
  try {
    await api.post(
      `/v1/users/${userId}/mfa/devices/${deviceId}/reactivate`,
      {},
      { headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined },
    )
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function adminEnrollMFADevice(
  userId: number,
  label: string,
  reauthToken?: string | null,
): Promise<MFAEnrollResponse> {
  try {
    const response = await api.post<MFAEnrollResponse>(
      `/v1/users/${userId}/mfa/enroll`,
      { device_label: label },
      { headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Self-service MFA Devices =====

export async function renameMFADevice(deviceId: number, newLabel: string): Promise<void> {
  try {
    await api.patch(`/v1/auth/mfa/devices/${deviceId}`, { label: newLabel })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function listMFADevices(): Promise<MFADevice[]> {
  try {
    const response = await api.get<MFADevice[]>('/v1/auth/mfa/devices')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function enrollMFADevice(data: MFAEnrollRequest): Promise<MFAEnrollResponse> {
  try {
    const response = await api.post<MFAEnrollResponse>('/v1/auth/mfa/enroll', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteMFADevice(deviceId: number, data: ReauthData): Promise<void> {
  try {
    // axios DELETE with request body requires the `data` config key
    await api.delete(`/v1/auth/mfa/devices/${deviceId}`, { data })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function rotateBackupCodes(data: ReauthData): Promise<string[]> {
  try {
    const response = await api.post<{ message: string; backup_codes: string[] }>(
      '/v1/auth/mfa/backup-codes/rotate',
      data
    )
    return response.data.backup_codes
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Sessions =====

export async function listSessions(): Promise<UserSession[]> {
  try {
    const response = await api.get<UserSession[]>('/v1/auth/sessions')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function logoutAllOtherSessions(): Promise<void> {
  try {
    await api.delete('/v1/auth/sessions')
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function logoutSession(sessionId: number): Promise<void> {
  try {
    await api.delete(`/v1/auth/sessions/${sessionId}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
